import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { Lookup } from 'src/app/shared/models/lookup.model';
import { buildHttpParams } from 'src/app/shared/utilities/interpolate-params';
import { combine } from 'src/app/shared/utilities/route';
import { ImportCandidateResultsDto, ImportCandidateType } from './models/workforce-disruption-upload-dialog-data.model';
import {
    CurrentTravelersSurveySentOptInStatusDto,
    OnsiteCandidateInformation,
    OptInStatusPayload,
    SurveySentStatusPayload,
    UpdateContractStatusData,
    WfdAdditionalInfo,
    WfdFacilityInfo,
    WfdFacilitySystemAdditionalInfo,
    WfdHotelDetails,
    WfdHotelDetailsPayload,
    WfdLoadLevelingCommentsPayload,
    WorkforceDisruptionPlacement,
    WorkforceDisruptionPlacementFlightBookingInformation,
    WorkforceDisruptionPlacementPermissions,
    WorkforceDisruptionPlacementTravelDetails,
    WorkforceDisruptionFacilitySystemPlacement,
    SearchSystemFilters
} from './models/workforce-disruption.model';
import { PersonalProfessionalInfo } from 'src/app/shared/components/personal-professional-info/models/personal-professional-info.model';
import { KeyValue } from '@angular/common';
import dayjs from 'dayjs';

@Injectable({ providedIn: 'root' })
export class WorkforceDisruptionService {
    private readonly _coreApiPath: string;
    private readonly _wfdApiPath: string;

    constructor(
        private readonly _httpClient: HttpClient,
        @Inject(APP_CONFIG) settings: Settings
    ) {
        this._coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
        this._wfdApiPath = combine(this._coreApiPath, 'workforce-disruption/contracts-placements');
    }

    searchBookedCandidates(
        facilityId: number,
        args: FetchArgs
    ): Observable<PagingToken<WorkforceDisruptionPlacement[]>> {
        const url = `${this._wfdApiPath}/${facilityId}`;
        const params = {};
        buildHttpParams(params, args, '');
        return this._httpClient.get<PagingToken<WorkforceDisruptionPlacement[]>>(url, { params });
    }

    getPermissions(): Observable<WorkforceDisruptionPlacementPermissions> {
        const url = `${this._wfdApiPath}/permissions`;
        return this._httpClient.get<WorkforceDisruptionPlacementPermissions>(url);
    }

    getStatuses(): Observable<Array<KeyValue<number, string>>> {
        const url = `${this._wfdApiPath}/statuses`;
        return this._httpClient.get<Array<KeyValue<number, string>>>(url);
    }

    export(facilityId: number): Observable<HttpResponse<Blob>> {
        const url = `${this._wfdApiPath}/${facilityId}/export`;
        return this._httpClient.get<Blob>(url, {
            observe: 'response',
            responseType: 'blob' as 'json'
        });
    }

    exportWithDetails(facilityId: number): Observable<HttpResponse<Blob>> {
        const url = `${this._wfdApiPath}/${facilityId}/export-with-details`;
        return this._httpClient.get<Blob>(url, {
            observe: 'response',
            responseType: 'blob' as 'json'
        });
    }

    exportCandidateImportError(facilityId: number, isNewSystemImportFF = false): Observable<HttpResponse<Blob>> {
        const url = isNewSystemImportFF
            ? `${this._wfdApiPath}/facility-systems/${facilityId}/import/error-report`
            : `${this._wfdApiPath}/${facilityId}/import/error-report`;
        return this._httpClient.get<Blob>(url, {
            observe: 'response',
            responseType: 'blob' as 'json'
        });
    }

    updateContractStatus(body: UpdateContractStatusData): Observable<number> {
        const url = `${this._wfdApiPath}`;
        return this._httpClient.put<number>(url, body);
    }

    updateReturnTravelStatus(body: UpdateContractStatusData): Observable<void> {
        const url = `${this._wfdApiPath}/return-travel-status`;
        return this._httpClient.put<void>(url, body);
    }

    getContractPlacementInformation(contractId: number): Observable<unknown> {
        const path = `${this._wfdApiPath}/${contractId}/placement-information`;
        return this._httpClient.get<unknown>(path);
    }

    getContractCandidateProfile(candidateId: number): Observable<PersonalProfessionalInfo> {
        const path = `${this._coreApiPath}/candidate/${candidateId}/profile?isSubContractor=false`;
        return this._httpClient.get<PersonalProfessionalInfo>(path);
    }

    getContractFlightBookingInformation(
        contractId: number
    ): Observable<WorkforceDisruptionPlacementFlightBookingInformation[]> {
        const path = `${this._wfdApiPath}/${contractId}/flight-booking-information`;
        return this._httpClient.get<WorkforceDisruptionPlacementFlightBookingInformation[]>(path);
    }

    updateContractFlightBookingInformation(
        contractId: number,
        data: WorkforceDisruptionPlacementFlightBookingInformation[]
    ): Observable<void> {
        const path = `${this._wfdApiPath}/${contractId}/flight-booking-information`;
        return this._httpClient.put<void>(path, data);
    }

    getContractTravelDetails(contractId: number): Observable<WorkforceDisruptionPlacementTravelDetails> {
        const path = `${this._wfdApiPath}/${contractId}/travel-details`;
        return this._httpClient.get<WorkforceDisruptionPlacementTravelDetails>(path);
    }

    updateContractTravelDetails(contractId: number, data: WorkforceDisruptionPlacementTravelDetails): Observable<void> {
        const path = `${this._wfdApiPath}/${contractId}/travel-details`;
        return this._httpClient.put<void>(path, data);
    }

    getWfdHotelDetails(contractId: number): Observable<WfdHotelDetails> {
        const path = `${this._wfdApiPath}/${contractId}/hotel-assignment-information`;
        return this._httpClient.get<WfdHotelDetails>(path);
    }

    updateWfdHotelDetails(contractId: number, data: WfdHotelDetailsPayload): Observable<void> {
        const path = `${this._wfdApiPath}/${contractId}/hotel-assignment-information`;
        return this._httpClient.put<void>(path, data);
    }

    updateWfdLoadLevelingComments(contractId: number, data: WfdLoadLevelingCommentsPayload): Observable<void> {
        const path = `${this._wfdApiPath}/${contractId}/comments`;
        return this._httpClient.post<void>(path, data);
    }

    getTravelStatuses(): Observable<Lookup[]> {
        const url = `${this._wfdApiPath}/lookup/travel-statuses`;
        return this._httpClient.get<Lookup[]>(url);
    }

    getTravelPlanTypes(): Observable<Lookup[]> {
        const url = `${this._wfdApiPath}/lookup/travel-plan-types`;
        return this._httpClient.get<Lookup[]>(url);
    }

    getGenderTypes(): Observable<Lookup[]> {
        const url = `${this._wfdApiPath}/lookup/gender-types`;
        return this._httpClient.get<Lookup[]>(url);
    }

    getStateCodes(): Observable<Lookup[]> {
        const url = `${this._wfdApiPath}/lookup/state-codes`;
        return this._httpClient.get<Lookup[]>(url);
    }

    uploadFile(
        facilityId: string,
        importFile: File,
        importType: ImportCandidateType,
        isNewSystemImportFF = false
    ): Observable<ImportCandidateResultsDto> {
        const url = isNewSystemImportFF
            ? `${this._wfdApiPath}/facility-systems/${facilityId}/import/${importType}`
            : `${this._wfdApiPath}/${facilityId}/import/${importType}`;
        const data = new FormData();
        data.append('file', importFile);
        return this._httpClient.post<ImportCandidateResultsDto>(url, data);
    }

    searchOnsiteTravelers(args: FetchArgs): Observable<PagingToken<OnsiteCandidateInformation[]>> {
        const url = `${this._wfdApiPath}/current-travelers`;
        const params = {};
        buildHttpParams(params, args, '');
        return this._httpClient.get<PagingToken<OnsiteCandidateInformation[]>>(url, { params });
    }

    facilitiesPlacementGridExport(args: FetchArgs): Observable<HttpResponse<Blob>> {
        const url = `${this._wfdApiPath}/facility-placements/export`;
        const params = {};
        buildHttpParams(params, args, '');
        return this._httpClient.get<Blob>(url, {
            params,
            observe: 'response',
            responseType: 'blob' as 'json'
        });
    }

    updateStrikeOptInStatus(payload: OptInStatusPayload): Observable<boolean> {
        const path = `${this._wfdApiPath}/current-traveler/opt-in-status`;
        return this._httpClient.put<boolean>(path, payload);
    }

    updateSurveySentStatus(payload: SurveySentStatusPayload): Observable<boolean> {
        const path = `${this._wfdApiPath}/current-traveler/survey-sent-status`;
        return this._httpClient.put<boolean>(path, payload);
    }

    bulkUpdateSurveySentOptInStatus(payload: CurrentTravelersSurveySentOptInStatusDto): Observable<boolean> {
        const path = `${this._wfdApiPath}/current-traveler/bulk/survey-sent-opt-in-status`;
        return this._httpClient.put<boolean>(path, payload);
    }

    getWfdAdditionalInfo(contractId: number): Observable<WfdAdditionalInfo[]> {
        const path = `${this._coreApiPath}/workforce-disruption/contracts-placements/${contractId}/additional-details`;
        return this._httpClient.get<WfdAdditionalInfo[]>(path);
    }

    updateWfdAdditionalInfo(contractId: number, additionalInfo: WfdAdditionalInfo[]): Observable<WfdAdditionalInfo[]> {
        const path = `${this._coreApiPath}/workforce-disruption/contracts-placements/${contractId}/additional-details`;
        return this._httpClient.post<WfdAdditionalInfo[]>(path, additionalInfo);
    }

    getWfdFacilitySystemAdditionalInfo(contractId: number): Observable<WfdFacilitySystemAdditionalInfo[]> {
        const path = `${this._coreApiPath}/workforce-disruption/contracts-placements/${contractId}/facility-system/additional-details`;
        return this._httpClient.get<WfdFacilitySystemAdditionalInfo[]>(path);
    }

    updateWfdFacilitySystemAdditionalInfo(
        additionalInfo: WfdFacilitySystemAdditionalInfo[]
    ): Observable<WfdFacilitySystemAdditionalInfo[]> {
        const path = `${this._coreApiPath}/workforce-disruption/facility-system-custom-fields/values`;
        return this._httpClient.put<WfdFacilitySystemAdditionalInfo[]>(path, additionalInfo);
    }

    getFacility(facilityId: number): Observable<WfdFacilityInfo> {
        const path = `${this._coreApiPath}/facility-contracts/facility-profiles/${facilityId}/profile`;
        return this._httpClient.get<WfdFacilityInfo>(path);
    }

    getSystemBookedCandidates(
        facilitySystemId: number,
        args: FetchArgs
    ): Observable<PagingToken<WorkforceDisruptionFacilitySystemPlacement[]>> {
        const url = `${this._wfdApiPath}/facility-system/${facilitySystemId}`;
        const params = {};
        buildHttpParams(params, args, '');
        return this._httpClient.get<PagingToken<WorkforceDisruptionFacilitySystemPlacement[]>>(url, { params });
    }

    getSystemBookedCandidatesV2(
        headerFilters: SearchSystemFilters,
        args: FetchArgs
    ): Observable<PagingToken<WorkforceDisruptionFacilitySystemPlacement[]>> {
        const url = `${this._wfdApiPath}/facility-system`;
        const body = {
            fetchArgs: this.buildFetchArgs(args),
            headerFilters: headerFilters
        };
        return this._httpClient.post<PagingToken<WorkforceDisruptionFacilitySystemPlacement[]>>(url, body);
    }

    getSystemFacilities(facilitySystemId: number): Observable<Lookup[]> {
        const url = `${this._wfdApiPath}/facility-systems/${facilitySystemId}/facilities`;
        return this._httpClient.get<Lookup[]>(url);
    }

    getSystemContractGroups(facilitySystemId: number): Observable<Lookup[]> {
        const url = `${this._wfdApiPath}/facility-systems/${facilitySystemId}/contract-groups`;
        return this._httpClient.get<Lookup[]>(url);
    }

    exportFacilitySystem(facilitySystemId: number, args: FetchArgs): Observable<HttpResponse<Blob>> {
        const url = `${this._wfdApiPath}/facility-systems/${facilitySystemId}/export`;
        const params = {};
        buildHttpParams(params, args, '');
        return this._httpClient.get<Blob>(url, {
            observe: 'response',
            responseType: 'blob' as 'json',
            params
        });
    }

    exportFacilitySystemV2(headerFilters: SearchSystemFilters, args: FetchArgs): Observable<HttpResponse<Blob>> {
        const url = `${this._wfdApiPath}/facility-systems/export`;
        const body = {
            fetchArgs: this.buildFetchArgs(args),
            headerFilters: headerFilters
        };
        return this._httpClient.post<Blob>(url, body, {
            observe: 'response',
            responseType: 'blob' as 'json'
        });
    }

    exportFacilitySystemWithDetails(facilitySystemId: number, args: FetchArgs): Observable<HttpResponse<Blob>> {
        const url = `${this._wfdApiPath}/facility-systems/${facilitySystemId}/export-with-details`;
        const params = {};
        buildHttpParams(params, args, '');
        return this._httpClient.get<Blob>(url, {
            observe: 'response',
            responseType: 'blob' as 'json',
            params
        });
    }

    exportFacilitySystemWithDetailsV2(
        headerFilters: SearchSystemFilters,
        args: FetchArgs
    ): Observable<HttpResponse<Blob>> {
        const url = `${this._wfdApiPath}/facility-systems/export-with-details`;
        const body = {
            fetchArgs: this.buildFetchArgs(args),
            headerFilters: headerFilters
        };
        return this._httpClient.post<Blob>(url, body, {
            observe: 'response',
            responseType: 'blob' as 'json'
        });
    }

    private buildFetchArgs(args: FetchArgs): FetchArgs {
        const customFields = {};
        const newFilter = {};
        for (const [key, value] of Object.entries(args.filter)) {
            if (!key.startsWith('*')) {
                newFilter[key] = value;
                continue;
            }
            const [, fieldName, suffix] = key.split('*');
            const field = (customFields[fieldName] ??= { fieldName });
            if (suffix === 'From') {
                field.fromDate =
                    typeof value === 'string' || value instanceof Date ? dayjs(value).format('MM-DD-YYYY') : undefined;
            } else if (suffix === 'To') {
                field.toDate =
                    typeof value === 'string' || value instanceof Date ? dayjs(value).format('MM-DD-YYYY') : undefined;
            } else if (Array.isArray(value)) {
                field.valueOptionIds = value;
            } else {
                field.fieldValue = value;
            }
        }
        if (Object.keys(customFields).length > 0) {
            newFilter['customFields'] = Object.values(customFields);
        }
        return {
            ...args,
            filter: newFilter
        };
    }
}
